import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Activity, ShieldCheck, KeyRound, Loader2, Monitor } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useApp } from "@/context/AppContext";

export default function Login() {
  const { login, activate, license, errMsg } = useApp();
  const nav = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [key, setKey] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const needsActivation = license && !license.activated;

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      if (needsActivation) {
        await activate(key);
      } else {
        await login(username.trim().toLowerCase(), password);
        nav("/");
      }
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr] bg-background">
      <div className="hidden lg:flex flex-col p-14 bg-[#0C1A24] text-slate-100 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07]"
          style={{ backgroundImage: "radial-gradient(#7DD3FC 1px, transparent 1px)", backgroundSize: "26px 26px" }} />
        <div className="relative">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-md bg-sky-500/15 border border-sky-400/30 grid place-items-center">
              <Activity className="h-6 w-6 text-sky-300" />
            </div>
            <div>
              <p className="font-heading text-xl font-semibold tracking-tight">Zuniva Care</p>
              <p className="text-xs text-slate-400 font-mono">ONLINE HOSPITAL INFORMATION SYSTEM</p>
            </div>
          </div>
        </div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="relative max-w-lg space-y-6 flex-1 flex flex-col justify-center py-16">
          <h1 className="font-heading text-4xl lg:text-5xl font-bold leading-[1.05] tracking-tight">
            Secure clinical records, built for your hospital.
          </h1>
          <p className="text-slate-400 leading-relaxed text-sm">
            A focused hospital information system designed for secure, local-first clinical workflows.
          </p>
          <div className="grid gap-3 pt-4 text-sm">
            {[["Secure local data storage", ShieldCheck],
              ["Device-locked activation & audit trail", Monitor],
              ["Role based access for every staff member", KeyRound]].map(([t, Icon]) => (
              <div key={t} className="flex items-center gap-3 text-slate-300">
                <Icon className="h-4 w-4 text-sky-400 shrink-0" />{t}
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      <div className="flex items-center justify-center p-8">
        <motion.form onSubmit={submit} data-testid="login-form"
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-sm space-y-6">
          <div className="space-y-1.5">
            <p className="text-xs font-mono uppercase tracking-widest text-sky-700 dark:text-sky-400">
              {needsActivation ? "Step 1 — Activation" : "Secure sign in"}
            </p>
            <h2 className="font-heading text-3xl font-bold tracking-tight">
              {needsActivation ? "Activate this device" : "Welcome back"}
            </h2>
            <p className="text-sm text-muted-foreground">
              {needsActivation
                ? "Enter the offline license key supplied with your installation."
                : "Use your staff credentials to open the console."}
            </p>
          </div>

          {needsActivation ? (
            <div className="space-y-2">
              <Label htmlFor="key">Activation key</Label>
              <Input id="key" data-testid="activation-key-input" value={key} autoFocus
                onChange={(e) => setKey(e.target.value)} placeholder=""
                className="font-mono" />
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label htmlFor="username">Username</Label>
                <Input id="username" data-testid="login-username-input" value={username} autoFocus
                  onChange={(e) => setUsername(e.target.value)} placeholder="User name" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input id="password" type="password" data-testid="login-password-input" value={password}
                  onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
              </div>
            </>
          )}

          {error && (
            <p data-testid="login-error" className="text-sm text-destructive border-l-2 border-destructive pl-3">
              {error}
            </p>
          )}

          <Button type="submit" disabled={busy} data-testid="login-submit-button"
            className="w-full h-11 transition-transform active:scale-[0.98]">
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {needsActivation ? "Activate application" : "Sign in"}
          </Button>

        </motion.form>
      </div>
    </div>
  );
}
